import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Calendar,
  CalendarDays,
  CheckCircle2,
  DollarSign,
  FileWarning,
  Handshake,
  HeartPulse,
  HelpCircle,
  Minus,
  MoreHorizontal,
  PlusCircle,
  TrendingDown,
  TrendingUp,
  Truck,
  XCircle,
  Zap,
} from 'lucide-react';

import {
  ProviderTreatmentStatus,
  TreatmentBarrierType,
  TreatmentFrequency,
  TreatmentProgress,
} from '@/services/types/treatment-types';

export interface BarrierType {
  value: TreatmentBarrierType;
  label: string;
  icon: React.ElementType;
  color: string;
}

export interface TreatmentInfo {
  label: string;
  icon: React.ElementType;
  color: string;
}

export const barrierTypes: BarrierType[] = [
  {
    value: TreatmentBarrierType.PROVIDER_ISSUE,
    label: 'Provider Issue',
    icon: HeartPulse,
    color: 'text-blue-500',
  },
  {
    value: TreatmentBarrierType.SCHEDULING_ISSUE,
    label: 'Scheduling Issue',
    icon: Calendar,
    color: 'text-purple-500',
  },
  {
    value: TreatmentBarrierType.TRANSPORTATION,
    label: 'Transportation Issue',
    icon: Truck,
    color: 'text-orange-500',
  },
  {
    value: TreatmentBarrierType.TREATMENT_ADHERENCE,
    label: 'Treatment Adherence Issue',
    icon: Handshake,
    color: 'text-indigo-500',
  },
  {
    value: TreatmentBarrierType.BILLING_INSURANCE,
    label: 'Billing/Insurance Issue',
    icon: DollarSign,
    color: 'text-green-500',
  },
  {
    value: TreatmentBarrierType.WORSENING_CONDITION,
    label: 'Worsening Condition',
    icon: TrendingDown,
    color: 'text-red-500',
  },
  {
    value: TreatmentBarrierType.NEW_INJURY,
    label: 'New Accident or Injury',
    icon: FileWarning,
    color: 'text-yellow-600',
  },
  {
    value: TreatmentBarrierType.OTHER,
    label: 'Other',
    icon: PlusCircle,
    color: 'text-gray-500',
  },
];

export const treatmentStatusInfo: Record<
  ProviderTreatmentStatus,
  TreatmentInfo
> = {
  [ProviderTreatmentStatus.TREATING]: {
    label: 'Still Treating',
    icon: Activity,
    color: 'text-blue-500',
  },
  [ProviderTreatmentStatus.COMPLETED]: {
    label: 'Completed',
    icon: CheckCircle2,
    color: 'text-green-500',
  },
  [ProviderTreatmentStatus.REFERRED_TO]: {
    label: 'Referred To',
    icon: ArrowRight,
    color: 'text-indigo-500',
  },
  [ProviderTreatmentStatus.DEVIATING]: {
    label: 'Deviating',
    icon: AlertTriangle,
    color: 'text-red-600',
  },
  [ProviderTreatmentStatus.UNKNOWN]: {
    label: 'Unknown',
    icon: HelpCircle,
    color: 'text-gray-500',
  },
  [ProviderTreatmentStatus.NEVER_TREATED]: {
    label: 'Never Treated',
    icon: XCircle,
    color: 'text-red-600',
  },
};

export const treatmentProgressInfo: Record<TreatmentProgress, TreatmentInfo> = {
  [TreatmentProgress.IMPROVING]: {
    label: 'Improving',
    icon: TrendingUp,
    color: 'text-green-500',
  },
  [TreatmentProgress.STAGNANT]: {
    label: 'Stagnant',
    icon: Minus,
    color: 'text-yellow-500',
  },
  [TreatmentProgress.REGRESSING]: {
    label: 'Regressing',
    icon: TrendingDown,
    color: 'text-red-500',
  },
};

export const treatmentFrequencyInfo: Record<TreatmentFrequency, TreatmentInfo> =
  {
    [TreatmentFrequency.DAILY]: {
      label: 'Daily',
      icon: CalendarDays,
      color: 'text-gray-700',
    },
    [TreatmentFrequency.WEEKLY]: {
      label: 'Weekly',
      icon: CalendarDays,
      color: 'text-gray-700',
    },
    [TreatmentFrequency.BI_WEEKLY]: {
      label: 'Bi-Weekly',
      icon: CalendarDays,
      color: 'text-gray-700',
    },
    [TreatmentFrequency.MONTHLY]: {
      label: 'Monthly',
      icon: Calendar,
      color: 'text-gray-700',
    },
    [TreatmentFrequency.AS_NEEDED]: {
      label: 'As Needed',
      icon: Zap,
      color: 'text-gray-700',
    },
    [TreatmentFrequency.OTHER]: {
      label: 'Other',
      icon: MoreHorizontal,
      color: 'text-gray-700',
    },
  };
